/**
 * 到家服务订单表格组件
 * 专门处理到家服务订单的数据结构和显示
 */
import React from 'react'
import { Table, Tag, Button, Space } from 'antd'
import { EyeOutlined, CheckCircleOutlined } from '@ant-design/icons'
import { SelectC, RangePickerC } from '@/i2antdV5'
import { renderTextStr, renderPhoneStr, formatDateTimeStr } from '@/utils/renderUtils'
import { ORDER_STATUS_CONFIG, SERVICE_TYPE_CONFIG, ORDER_STATUS_OPTIONS, SERVICE_TYPE_OPTIONS } from '../config/orderTypeConfig'
import HandleAbnormalOrderModal from '../share/HandleAbnormalOrderModal'
import OrderDetailDrawer from '../share/OrderDetailDrawer'

class HomeServiceOrderTable extends React.Component {
  /**
   * 获取表格列配置
   * 根据到家服务订单的数据结构定制
   */
  getColumns = () => {
    return [
      {
        title: '订单号',
        dataIndex: 'orderNo',
        key: 'orderNo',
        width: 150,
        fixed: 'left',
        render: (text) => renderTextStr(text)
      },
      {
        title: '客户信息',
        key: 'customerInfo',
        width: 180,
        render: (_, record) => {
          const name = renderTextStr(record.customerName)
          const phone = renderPhoneStr(record.customerPhone)
          return (
            <div>
              <div>{name}</div>
              <div style={{ color: '#999', fontSize: '12px' }}>{phone}</div>
            </div>
          )
        }
      },
      {
        title: '门店',
        dataIndex: 'storeName',
        key: 'storeName',
        width: 180,
        render: (text) => renderTextStr(text)
      },
      {
        title: '服务类型',
        dataIndex: 'serviceType',
        key: 'serviceType',
        width: 100,
        render: (serviceType) => {
          const config = SERVICE_TYPE_CONFIG[serviceType] || {}
          return <Tag color={config.color}>{config.text || serviceType}</Tag>
        }
      },
      {
        title: '订单状态',
        dataIndex: 'status',
        key: 'status',
        width: 120,
        render: (status) => {
          const config = ORDER_STATUS_CONFIG[status] || {}
          return <Tag color={config.color}>{config.text || status}</Tag>
        }
      },
      {
        title: '接单人员',
        dataIndex: 'staffName',
        key: 'staffName',
        width: 120,
        render: (text) => renderTextStr(text)
      },
      {
        title: '订单金额',
        dataIndex: 'amount',
        key: 'amount',
        width: 120,
        align: 'right',
        render: (amount) => amount ? `¥${Number(amount).toFixed(2)}` : '-'
      },
      {
        title: '创建时间',
        dataIndex: 'createTime',
        key: 'createTime',
        width: 160,
        render: (text) => formatDateTimeStr(text)
      },
      {
        title: '服务时间',
        key: 'serviceTime',
        width: 200,
        render: (_, record) => {
          const startTime = record.startTime ? formatDateTimeStr(record.startTime) : '-'
          const endTime = record.endTime ? formatDateTimeStr(record.endTime) : '-'
          return (
            <div>
              <div>开始：{startTime}</div>
              <div style={{ color: '#999', fontSize: '12px' }}>结束：{endTime}</div>
            </div>
          )
        }
      },
      {
        title: '服务地址',
        dataIndex: 'address',
        key: 'address',
        width: 200,
        render: (text) => renderTextStr(text)
      },
      {
        title: '操作',
        key: 'action',
        width: 150,
        fixed: 'right',
        render: (_, record) => {
          return (
            <Space size="small">
              <OrderDetailDrawer
                baseProps={{
                  orderId: record.id,
                  onClose: () => {}
                }}
              >
                <Button
                  type="link"
                  size="small"
                  icon={<EyeOutlined />}
                >
                  详情
                </Button>
              </OrderDetailDrawer>
              {record.isAbnormal && (
                <HandleAbnormalOrderModal
                  baseProps={{
                    orderId: record.id,
                    orderDetail: record,
                    onSuccess: this.props.onAbnormalSuccess || (() => {})
                  }}
                >
                  <Button
                    type="link"
                    size="small"
                    danger
                    icon={<CheckCircleOutlined />}
                  >
                    处理
                  </Button>
                </HandleAbnormalOrderModal>
              )}
            </Space>
          )
        }
      }
    ]
  }

  /**
   * 数据处理：将API返回的数据转换为表格需要的格式
   * 到家服务订单可能有不同的数据结构
   */
  processData = (dataSource) => {
    return dataSource.map(item => ({
      ...item,
      // 如果API返回的字段名不同，在这里转换
      // 例如：address: item.service_address || item.address,
    }))
  }

  /**
   * 渲染筛选条件
   */
  renderFilters = () => {
    const { query = {}, onQueryChange, jStoreOptions = [] } = this.props
    
    return (
      <section className="i2-app-toolbar">
        <div className="toolbar-filters">
          <SelectC
            label="门店"
            query={query}
            searchKey="storeId"
            options={jStoreOptions}
            action={onQueryChange}
          />
          <SelectC
            label="订单状态"
            query={query}
            searchKey="status"
            options={ORDER_STATUS_OPTIONS}
            action={onQueryChange}
            placeholder="订单状态"
          />
          <SelectC
            label="服务类型"
            query={query}
            searchKey="serviceType"
            options={SERVICE_TYPE_OPTIONS}
            action={onQueryChange}
            placeholder="服务类型"
            modalProps={{
              mode: 'multiple',
              style: { width: 240 },
            }}
          />
          <RangePickerC
            label="时间范围"
            query={query}
            searchKey={['startTime', 'endTime']}
            action={onQueryChange}
            placeholder={['开始时间', '结束时间']}
          />
        </div>
      </section>
    )
  }

  render() {
    const {
      loading = false,
      dataSource = [],
      pagination,
      onPageChange,
      ...restProps
    } = this.props

    const columns = this.getColumns()
    const processedData = this.processData(dataSource)

    return (
      <>
        {this.renderFilters()}
        <Table
          loading={loading}
          columns={columns}
          dataSource={processedData}
          rowKey="id"
          scroll={{ x: 1500 }}
          size="small"
          pagination={{
            ...pagination,
            showSizeChanger: true,
            showTotal: (total) => `共 ${total} 条`,
            onChange: onPageChange,
            onShowSizeChange: (current, size) => onPageChange && onPageChange(1, size)
          }}
          {...restProps}
        />
      </>
    )
  }
}

export default HomeServiceOrderTable
